import React, { useState, useEffect, useCallback, useContext } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Paper,
  Chip,
  TextField,
  MenuItem,
  Divider,
  CircularProgress,
  Typography,
  Collapse,
  IconButton
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { useTranslation } from 'react-i18next'
import CPText from '../../../Utils/CPText'
import CPTip from '../../../Utils/CPTip'
import DocAlertScheduleForm from './DocAlertScheduleForm'
import DocAlertRecipientsForm from './DocAlertRecipientsForm'
import { formatDate } from '../../../../Utils/Format'
import { listSuggestionsByClientId } from '../../../../services/alerts'
import {
  deleteRule,
  getRuleById,
  getRulesByDocId,
  getTemplateById,
  getTemplatesByUserAndDocument,
  insertRule,
  insertRuleRecipient,
  insertRuleSchedule,
  updateRuleSchedule,
  updateRule,
  deleteRuleRecipient
} from '../../../../services/notificationRules'
import CPDeleteIconButton from '../../../Utils/CPDeleteIconButton'
import CPButton from '../../../Utils/CPButton'
import TemplateEditorDialog from './TemplateEditorDialog'
import { authContext } from '../../../../contexts/AuthContext'

const brandColor = () => (window.$brand === 'PRB' ? '#17afb6' : '#102a46')
const brandRadius = () => (window.$brand === 'PRB' ? 0 : 5)

const EMPTY_SCHEDULE = {
  trigger_type: '',
  offset_days: '',
  scheduled_at: null,
  recurrence_rule: '',
  recurrence_end: null
}

// ----------------------------------------------------------------
// RuleCard — displays a single saved rule with its schedules
// ----------------------------------------------------------------
function RuleCard({ rule, onDelete, onEdit, t }) {
  const [expanded, setExpanded] = useState(false)

  const DAY_NAMES = {
    MO: 'Monday',
    TU: 'Tuesday',
    WE: 'Wednesday',
    TH: 'Thursday',
    FR: 'Friday',
    SA: 'Saturday',
    SU: 'Sunday'
  }
  const MONTH_NAMES = [
    '',
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ]
  const NTH = ['', 'first', 'second', 'third', 'fourth', 'fifth', 'last']

  function parseRRule(rrule) {
    if (!rrule) return {}
    return Object.fromEntries(rrule.split(';').map((part) => part.split('=')))
  }

  function rruleToHuman(rrule) {
    if (!rrule) return ''
    const p = parseRRule(rrule)

    switch (p.FREQ) {
      case 'DAILY':
        return p.INTERVAL ? `Every ${p.INTERVAL} days` : 'Daily'

      case 'WEEKLY': {
        const days = p.BYDAY
          ? p.BYDAY.split(',')
              .map((d) => DAY_NAMES[d] || d)
              .join(', ')
          : ''
        return days ? `Weekly on ${days}` : 'Weekly'
      }

      case 'MONTHLY': {
        if (p.BYDAY) {
          const match = p.BYDAY.match(/(-?\d+)([A-Z]+)/)
          if (match) {
            const n = parseInt(match[1])
            const day = DAY_NAMES[match[2]] || match[2]
            const label = n === -1 ? 'last' : NTH[n] || `${n}th`
            return `Monthly on the ${label} ${day}`
          }
        }
        if (p.BYMONTHDAY) {
          const d = parseInt(p.BYMONTHDAY)
          return d === -1 ? 'Monthly on the last day' : `Monthly on the ${d}th`
        }
        return 'Monthly'
      }

      case 'YEARLY': {
        const month = p.BYMONTH ? MONTH_NAMES[parseInt(p.BYMONTH)] : ''
        const day = p.BYMONTHDAY ? parseInt(p.BYMONTHDAY) : ''
        return month && day ? `Annually on ${month} ${day}` : 'Annually'
      }

      default:
        return rrule
    }
  }

  const scheduleLabel = (s) => {
    if (s.trigger_type === 'before_expiry')
      return `${s.offset_days} ${t('days before expiry')}`
    if (s.trigger_type === 'fixed_date') return formatDate(t, s.scheduled_at)
    if (s.trigger_type === 'recurring') {
      const pattern = rruleToHuman(s.recurrence_rule)
      const end = s.recurrence_end
        ? ` until ${formatDate(t, s.recurrence_end)}`
        : ''
      const start = s.scheduled_at
        ? ` from ${formatDate(t, s.scheduled_at)}`
        : ''
      return `${pattern}${start}${end}`
    }
    return s.trigger_type
  }

  const recipientLabel = (r) => {
    if (r.user_name) return `${r.user_name}`
    return r.email || ''
  }

  return (
    <Paper elevation={2} sx={{ p: 2, width: '100%', mb: 1.5 }}>
      <Grid container alignItems='center'>
        <Grid item xs={10}>
          <CPText
            label={t('Template')}
            text={rule.template_description || `#${rule.template_id}`}
          />
        </Grid>
        <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton size='small' onClick={() => setExpanded((v) => !v)}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <IconButton size='small' onClick={() => onEdit(rule)}>
            <EditOutlinedIcon fontSize='small' />
          </IconButton>
          <CPDeleteIconButton onClick={() => onDelete(rule.id)} />
        </Grid>

        {/* Schedules summary */}
        <Grid item xs={12} sx={{ mt: 0.5 }}>
          <Typography variant='caption' color='text.secondary'>
            {t('Schedules')}:{' '}
          </Typography>
          {rule.schedules?.length > 0 ? (
            rule.schedules.map((s) => (
              <Chip
                key={s.id}
                label={scheduleLabel(s)}
                size='small'
                sx={{ mr: 0.5 }}
              />
            ))
          ) : (
            <Typography variant='caption' color='text.secondary'>
              {t('None')}
            </Typography>
          )}
        </Grid>

        {/* Expanded: recipients */}
        <Grid item xs={12}>
          <Collapse in={expanded}>
            <Box sx={{ mt: 1.5 }}>
              <Divider sx={{ mb: 1 }} />
              <Typography
                variant='caption'
                color='text.secondary'
                display='block'
                sx={{ mb: 0.5 }}
              >
                {t('Recipients')}
              </Typography>
              <Box
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}
              >
                {rule.recipients
                  ?.filter((r) => !r.is_excluded)
                  .map((r) => (
                    <Chip key={r.id} label={recipientLabel(r)} size='small' />
                  ))}
                {rule.recipients?.filter((r) => !r.is_excluded).length ===
                  0 && (
                  <Typography variant='caption' color='text.secondary'>
                    {t('None')}
                  </Typography>
                )}
              </Box>
              {rule.recipients?.some((r) => r.is_excluded) && (
                <>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    display='block'
                    sx={{ mb: 0.5 }}
                  >
                    {t('Excluded')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {rule.recipients
                      .filter((r) => r.is_excluded)
                      .map((r) => (
                        <Chip
                          key={r.id}
                          label={recipientLabel(r)}
                          size='small'
                          color='error'
                          variant='outlined'
                        />
                      ))}
                  </Box>
                </>
              )}
            </Box>
          </Collapse>
        </Grid>
      </Grid>
    </Paper>
  )
}

// ----------------------------------------------------------------
// AddRuleDialog — form to create a new doc alert rule
// ----------------------------------------------------------------
function AddRuleDialog({
  open,
  onClose,
  onSaved,
  documentId,
  clientId,
  templates
}) {
  const { t } = useTranslation()

  const [templates_, setTemplates_] = useState(templates || [])
  const [suggestions, setSuggestions] = useState([])
  const [templateId, setTemplateId] = useState('')
  const [schedule, setSchedule] = useState(EMPTY_SCHEDULE)
  const [recipients, setRecipients] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false)
  const [templateEditorTarget, setTemplateEditorTarget] = useState(null) // null = create mode

  const selectedTemplate = templates_.find((t) => t.id === templateId) || null

  const handleOpenCreate = () => {
    setTemplateEditorTarget(null)
    setTemplateEditorOpen(true)
  }

  const handleOpenEditOrPreview = () => {
    setTemplateEditorTarget(selectedTemplate)
    setTemplateEditorOpen(true)
  }

  useEffect(() => {
    if (open) {
      setTemplateId('')
      setSchedule(EMPTY_SCHEDULE)
      setRecipients([])
      setError('')
      setTemplateEditorTarget(null)
    }
  }, [open])

  useEffect(() => {
    setTemplates_(templates || [])
  }, [templates])

  useEffect(() => {
    if (!open) return
    listSuggestionsByClientId(clientId)
      .then(setSuggestions)
      .catch(() => {})
  }, [open, clientId])

  const handleAddRecipient = (r) => {
    setRecipients((prev) => {
      // avoid duplicates by email
      if (r.email && prev.find((p) => p.email === r.email)) return prev
      return [...prev, r]
    })
  }

  const handleRemoveRecipient = (localId) => {
    setRecipients((prev) => prev.filter((r) => r._local_id !== localId))
  }

  const validate = () => {
    if (!templateId) return t('Please select a template')
    if (!schedule.trigger_type) return t('Please select a trigger type')
    if (schedule.trigger_type === 'before_expiry' && !schedule.offset_days)
      return t('Please enter the number of days before expiry')
    if (schedule.trigger_type === 'fixed_date' && !schedule.scheduled_at)
      return t('Please select a date')
    if (schedule.trigger_type === 'recurring' && !schedule.recurrence_rule)
      return t('Please select a recurrence pattern')
    if (recipients.filter((r) => !r.is_excluded).length === 0)
      return t('Please add at least one recipient')
    return null
  }

  const handleSave = async () => {
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }
    setError('')
    setSaving(true)

    try {
      // 1. Create the rule
      const ruleRes = await insertRule({
        type: 'doc_alert',
        document_id: documentId,
        template_id: parseInt(templateId),
        is_active: true
      })
      const ruleId = ruleRes.id

      // 2. Create the schedule
      await insertRuleSchedule(ruleId, {
        trigger_type: schedule.trigger_type,
        offset_days: schedule.offset_days || null,
        scheduled_at: schedule.scheduled_at || null,
        recurrence_rule: schedule.recurrence_rule || null,
        recurrence_end: schedule.recurrence_end || null
      })

      // 3. Create recipients
      await Promise.all(
        recipients.map((r) =>
          insertRuleRecipient(ruleId, {
            recipient_type: r.recipient_type,
            email: r.email || null,
            user_id: r.user_id || null,
            client_id: r.client_id || null,
            is_excluded: r.is_excluded
          })
        )
      )

      onSaved()
      handleClose()
    } catch (e) {
      setError(t('An error occurred. Please try again.'))
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    setTemplateId('')
    setSchedule(EMPTY_SCHEDULE)
    setRecipients([])
    setError('')
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>{t('Add alert')}</DialogTitle>
      <DialogContent>
        <CPTip
          text={t('Configure when and who receives this document alert')}
        />

        {/* Template selector */}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            select
            fullWidth
            size='small'
            label={t('Email template')}
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
          >
            {templates_.filter((tmpl) => tmpl.is_system).length > 0 && (
              <MenuItem disabled value='__system_header__'>
                <Typography variant='caption' color='text.secondary'>
                  {t('System templates')}
                </Typography>
              </MenuItem>
            )}
            {templates_
              .filter((tmpl) => tmpl.is_system)
              .map((tmpl) => (
                <MenuItem key={tmpl.id} value={tmpl.id}>
                  {tmpl.description || `Template #${tmpl.id}`}
                </MenuItem>
              ))}
            {templates_.filter((tmpl) => !tmpl.is_system && !tmpl.is_hidden)
              .length > 0 && (
              <MenuItem disabled value='__mine_header__'>
                <Typography variant='caption' color='text.secondary'>
                  {t('My templates')}
                </Typography>
              </MenuItem>
            )}
            {templates_
              .filter((tmpl) => !tmpl.is_system && !tmpl.is_hidden)
              .map((tmpl) => (
                <MenuItem key={tmpl.id} value={tmpl.id}>
                  {tmpl.description || `Template #${tmpl.id}`}
                </MenuItem>
              ))}
            {/* Hidden templates aren't listed for picking, but the one
                currently assigned still needs a MenuItem so the closed
                Select can render its label instead of showing blank. */}
            {selectedTemplate?.is_hidden && (
              <MenuItem value={selectedTemplate.id}>
                {selectedTemplate.description ||
                  `Template #${selectedTemplate.id}`}
              </MenuItem>
            )}
          </TextField>

          {/* Show eye icon for system templates, edit icon for own templates */}
          {selectedTemplate && (
            <CPButton
              icon={
                selectedTemplate.is_system ? (
                  <VisibilityOutlinedIcon />
                ) : (
                  <EditOutlinedIcon />
                )
              }
              tooltip={
                selectedTemplate.is_system
                  ? t('Template')
                  : t('Edit template')
              }
              onClick={handleOpenEditOrPreview}
            />
          )}

          {/* Create new template */}
          <CPButton
            icon={<AddIcon />}
            tooltip={t('Create new template')}
            onClick={handleOpenCreate}
          />
        </Box>

        <TemplateEditorDialog
          open={templateEditorOpen}
          onClose={() => setTemplateEditorOpen(false)}
          template={templateEditorTarget}
          onSaved={(savedTemplate) => {
            if (templateEditorTarget) {
              // update existing entry in list
              setTemplates_((prev) =>
                prev.map((t) =>
                  t.id === savedTemplate.id
                    ? { ...t, description: savedTemplate.description }
                    : t
                )
              )
            } else {
              // add newly created template and auto-select it
              setTemplates_((prev) => [...prev, savedTemplate])
              setTemplateId(savedTemplate.id)
            }
            setTemplateEditorOpen(false)
          }}
          isSystem={false}
          isHidden
        />

        {/* Schedule form */}
        <DocAlertScheduleForm value={schedule} onChange={setSchedule} />

        <Divider sx={{ my: 2 }} />

        {/* Recipients form */}
        <DocAlertRecipientsForm
          recipients={recipients}
          suggestions={suggestions}
          onAdd={handleAddRecipient}
          onRemove={handleRemoveRecipient}
        />

        {error && (
          <Typography
            variant='caption'
            color='error'
            sx={{ mt: 1, display: 'block' }}
          >
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          {t('Cancel')}
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function EditRuleDialog({ open, onClose, onSaved, rule, templates, clientId }) {
  const { t } = useTranslation()

  const [templates_, setTemplates_] = useState(templates || [])
  const [suggestions, setSuggestions] = useState([])
  const [templateId, setTemplateId] = useState('')
  const [schedule, setSchedule] = useState(EMPTY_SCHEDULE)
  const [recipients, setRecipients] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false)
  const [templateEditorTarget, setTemplateEditorTarget] = useState(null)

  const selectedTemplate =
    templates_.find((tmpl) => tmpl.id === templateId) || null

  useEffect(() => {
    setTemplates_(templates || [])
  }, [templates])

  // Load suggestions when opened
  useEffect(() => {
    if (!open) return
    listSuggestionsByClientId(clientId)
      .then(setSuggestions)
      .catch(() => {})
  }, [open, clientId])

  // Hydrate form from the rule when opened
  useEffect(() => {
    if (!open || !rule) return

    setTemplateId(rule.template_id || '')
    setError('')

    // A hidden template (created inline for a single alert) is excluded from
    // getTemplatesByUser, so fetch it directly to resolve it in the picker.
    if (
      rule.template_id &&
      !(templates || []).find((tmpl) => tmpl.id === rule.template_id)
    ) {
      getTemplateById(rule.template_id)
        .then((tmpl) => {
          setTemplates_((prev) =>
            prev.find((t) => t.id === tmpl.id) ? prev : [...prev, tmpl]
          )
        })
        .catch(() => {})
    }

    const s = rule.schedules?.[0]
    setSchedule(
      s
        ? {
            trigger_type: s.trigger_type || '',
            offset_days: s.offset_days || '',
            scheduled_at: s.scheduled_at || null,
            recurrence_rule: s.recurrence_rule || '',
            recurrence_end: s.recurrence_end || null
          }
        : EMPTY_SCHEDULE
    )

    // Map existing recipients into the local shape the form expects
    setRecipients(
      (rule.recipients || []).map((r) => ({
        recipient_type: r.recipient_type,
        email: r.email || null,
        user_id: r.user_id || null,
        client_id: r.client_id || null,
        user_name: r.user_name || null,
        is_excluded: r.is_excluded,
        _local_id: r.user_id || r.email || `r_${r.id}`
      }))
    )
  }, [open, rule, templates])

  const handleAddRecipient = (r) => {
    setRecipients((prev) => {
      if (r.email && prev.find((p) => p.email === r.email)) return prev
      if (r.user_id && prev.find((p) => p.user_id === r.user_id)) return prev
      return [...prev, r]
    })
  }

  const handleRemoveRecipient = (localId) => {
    setRecipients((prev) => prev.filter((r) => r._local_id !== localId))
  }

  const handleOpenCreate = () => {
    setTemplateEditorTarget(null)
    setTemplateEditorOpen(true)
  }

  const handleOpenEditOrPreview = () => {
    setTemplateEditorTarget(selectedTemplate)
    setTemplateEditorOpen(true)
  }

  const validate = () => {
    if (!templateId) return t('Please select a template')
    if (!schedule.trigger_type) return t('Please select a trigger type')
    if (schedule.trigger_type === 'before_expiry' && !schedule.offset_days)
      return t('Please enter the number of days before expiry')
    if (schedule.trigger_type === 'fixed_date' && !schedule.scheduled_at)
      return t('Please select a date')
    if (schedule.trigger_type === 'recurring' && !schedule.recurrence_rule)
      return t('Please select a recurrence pattern')
    if (recipients.filter((r) => !r.is_excluded).length === 0)
      return t('Please add at least one recipient')
    return null
  }

  const handleSave = async () => {
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }
    setError('')
    setSaving(true)

    try {
      // 1. Update the rule's template
      await updateRule(rule.id, { template_id: parseInt(templateId) })

      // 2. Update (or insert) the schedule
      if (rule.schedules?.[0]) {
        await updateRuleSchedule(rule.schedules[0].id, {
          trigger_type: schedule.trigger_type,
          offset_days: schedule.offset_days || null,
          scheduled_at: schedule.scheduled_at || null,
          recurrence_rule: schedule.recurrence_rule || null,
          recurrence_end: schedule.recurrence_end || null
        })
      } else {
        await insertRuleSchedule(rule.id, {
          trigger_type: schedule.trigger_type,
          offset_days: schedule.offset_days || null,
          scheduled_at: schedule.scheduled_at || null,
          recurrence_rule: schedule.recurrence_rule || null,
          recurrence_end: schedule.recurrence_end || null
        })
      }

      // 3. Replace recipients: delete the old ones, insert the current set
      await Promise.all(
        (rule.recipients || []).map((r) => deleteRuleRecipient(r.id))
      )
      await Promise.all(
        recipients.map((r) =>
          insertRuleRecipient(rule.id, {
            recipient_type: r.recipient_type,
            email: r.email || null,
            user_id: r.user_id || null,
            client_id: r.client_id || null,
            is_excluded: r.is_excluded
          })
        )
      )

      onSaved()
      onClose()
    } catch (e) {
      setError(t('An error occurred. Please try again.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{t('Edit alert')}</DialogTitle>
      <DialogContent>
        {/* Template selector */}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            select
            fullWidth
            size='small'
            label={t('Email template')}
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
          >
            {templates_.filter((tmpl) => tmpl.is_system).length > 0 && (
              <MenuItem disabled value='__system_header__'>
                <Typography variant='caption' color='text.secondary'>
                  {t('System templates')}
                </Typography>
              </MenuItem>
            )}
            {templates_
              .filter((tmpl) => tmpl.is_system)
              .map((tmpl) => (
                <MenuItem key={tmpl.id} value={tmpl.id}>
                  {tmpl.description || `Template #${tmpl.id}`}
                </MenuItem>
              ))}
            {templates_.filter((tmpl) => !tmpl.is_system && !tmpl.is_hidden)
              .length > 0 && (
              <MenuItem disabled value='__mine_header__'>
                <Typography variant='caption' color='text.secondary'>
                  {t('My templates')}
                </Typography>
              </MenuItem>
            )}
            {templates_
              .filter((tmpl) => !tmpl.is_system && !tmpl.is_hidden)
              .map((tmpl) => (
                <MenuItem key={tmpl.id} value={tmpl.id}>
                  {tmpl.description || `Template #${tmpl.id}`}
                </MenuItem>
              ))}
            {/* Hidden templates aren't listed for picking, but the one
                currently assigned still needs a MenuItem so the closed
                Select can render its label instead of showing blank. */}
            {selectedTemplate?.is_hidden && (
              <MenuItem value={selectedTemplate.id}>
                {selectedTemplate.description ||
                  `Template #${selectedTemplate.id}`}
              </MenuItem>
            )}
          </TextField>

          {selectedTemplate && (
            <CPButton
              icon={
                selectedTemplate.is_system ? (
                  <VisibilityOutlinedIcon />
                ) : (
                  <EditOutlinedIcon />
                )
              }
              tooltip={
                selectedTemplate.is_system
                  ? t('Template')
                  : t('Edit template')
              }
              onClick={handleOpenEditOrPreview}
            />
          )}

          <CPButton
            icon={<AddIcon />}
            tooltip={t('Create new template')}
            onClick={handleOpenCreate}
          />
        </Box>

        <TemplateEditorDialog
          open={templateEditorOpen}
          onClose={() => setTemplateEditorOpen(false)}
          template={templateEditorTarget}
          onSaved={(savedTemplate) => {
            if (templateEditorTarget) {
              setTemplates_((prev) =>
                prev.map((tmpl) =>
                  tmpl.id === savedTemplate.id
                    ? { ...tmpl, description: savedTemplate.description }
                    : tmpl
                )
              )
            } else {
              setTemplates_((prev) => [...prev, savedTemplate])
              setTemplateId(savedTemplate.id)
            }
            setTemplateEditorOpen(false)
          }}
          isSystem={false}
          isHidden
        />

        {/* Schedule form */}
        <DocAlertScheduleForm value={schedule} onChange={setSchedule} />

        <Divider sx={{ my: 2 }} />

        {/* Recipients form */}
        <DocAlertRecipientsForm
          recipients={recipients}
          suggestions={suggestions}
          onAdd={handleAddRecipient}
          onRemove={handleRemoveRecipient}
        />

        {error && (
          <Typography
            variant='caption'
            color='error'
            sx={{ mt: 1, display: 'block' }}
          >
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('Cancel')}
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ----------------------------------------------------------------
// DocAlerts — main component, mirrors the old Alerts component API
// ----------------------------------------------------------------
export default function DocAlerts({ open, onClose, docId, clientId }) {
  const { t } = useTranslation()
  const [rules, setRules] = useState([])
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [editRule, setEditRule] = useState(null)

  const { auth } = useContext(authContext)

  const fetchRules = useCallback(async () => {
    if (!docId) return
    setLoading(true)
    try {
      const list = await getRulesByDocId(docId)
      if (!Array.isArray(list)) {
        setRules([])
        return
      }
      const full = await Promise.all(list.map((r) => getRuleById(r.id)))
      console.log('full rules', JSON.stringify(full)) // add this
      setRules(full.filter(Boolean))
    } catch (e) {
      setRules([])
    } finally {
      setLoading(false)
    }
  }, [docId])

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await getTemplatesByUserAndDocument(auth.user.id, docId)
      setTemplates(Array.isArray(res) ? res : [])
    } catch (e) {}
  }, [docId])

  useEffect(() => {
    if (open) {
      fetchRules()
      fetchTemplates()
    }
  }, [open, fetchRules, fetchTemplates])

  const handleDelete = async (ruleId) => {
    await deleteRule(ruleId)
    setRules((prev) => prev.filter((r) => r.id !== ruleId))
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationsOutlinedIcon fontSize='small' />
          {t('Alerts')}
        </DialogTitle>

        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {rules.length === 0 && (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mb: 2 }}
                >
                  {t('No alerts configured for this document')}
                </Typography>
              )}

              {rules.map((rule) => (
                <RuleCard
                  key={rule.id}
                  rule={rule}
                  onDelete={handleDelete}
                  onEdit={setEditRule}
                  t={t}
                />
              ))}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={onClose}
            sx={{
              mt: 1
            }}
          >
            {t('Close')}
          </Button>

          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}
            sx={{
              mt: 1,
              backgroundColor: brandColor(),
              borderRadius: brandRadius()
            }}
          >
            {t('Add alert')}
          </Button>
        </DialogActions>
      </Dialog>

      <AddRuleDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={fetchRules}
        documentId={docId}
        clientId={clientId}
        templates={templates}
      />
      <EditRuleDialog
        open={!!editRule}
        onClose={() => setEditRule(null)}
        onSaved={() => {
          setEditRule(null)
          fetchRules()
        }}
        rule={editRule}
        templates={templates}
        clientId={clientId}
      />
    </>
  )
}
